import { Competitor } from '../types/competitors';

/**
 * Parses and normalizes suggested identities without generating IDs or saving data.
 * @param answer Raw JSON returned by the suggestion assistant.
 * @returns Validated suggestions without persistence identifiers.
 */
export const parseSuggestedCompetitors = (
    answer: string
): Omit<Competitor, 'id'>[] => {
    const response: unknown = JSON.parse(answer);
    if (
        !response ||
        typeof response !== 'object' ||
        !('competitors' in response) ||
        !Array.isArray(response.competitors)
    ) {
        throw new Error('Invalid competitor suggestions response.');
    }

    return response.competitors.map((suggestion: unknown) => {
        if (
            !suggestion ||
            typeof suggestion !== 'object' ||
            !('competitorName' in suggestion) ||
            !('city' in suggestion) ||
            !('country' in suggestion) ||
            !('starRating' in suggestion)
        ) {
            throw new Error('Invalid competitor suggestion.');
        }
        const { competitorName, city, country, starRating } = suggestion;
        if (
            typeof competitorName !== 'string' ||
            typeof city !== 'string' ||
            typeof country !== 'string' ||
            typeof starRating !== 'string' ||
            !competitorName.trim() ||
            !city.trim() ||
            !country.trim() ||
            !starRating.trim()
        ) {
            throw new Error('Invalid competitor suggestion details.');
        }
        return {
            query: competitorName.trim(),
            name: competitorName.trim(),
            city: city.trim(),
            country: country.trim(),
            starRating: starRating.trim(),
            source: 'suggested',
        };
    });
};

const normalize = (value?: string) => value?.trim().toLowerCase() || '';

const getIdentity = (competitor: Competitor) =>
    JSON.stringify(
        [competitor.name, competitor.city, competitor.country].map(normalize)
    );

/**
 * Selects new or incomplete candidates to save without mutating existing records.
 * @param suggestions Normalized candidates with generated identifiers.
 * @param competitors Existing identities and their optional analyses.
 * @returns Deduplicated updates, preserving existing identifiers and metadata.
 */
export const mergeSuggestedCompetitors = (
    suggestions: Competitor[],
    competitors: Competitor[]
): Competitor[] => {
    const existingByIdentity = new Map(
        competitors.map(competitor => [getIdentity(competitor), competitor])
    );
    const updates = new Map<string, Competitor>();
    for (const suggestion of suggestions) {
        const identity = getIdentity(suggestion);
        const existing = existingByIdentity.get(identity);
        if (existing?.city && existing.country && existing.starRating) {
            continue;
        }
        updates.set(
            identity,
            existing
                ? {
                      ...existing,
                      city: suggestion.city,
                      country: suggestion.country,
                      starRating: suggestion.starRating,
                  }
                : suggestion
        );
    }
    return [...updates.values()];
};
